import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Drawer } from './ui/Drawer';
import { CURRENCIES } from '../lib/catalogs';
import { useCreateBankAccount } from '../hooks/useBankFeed';

const inputCn =
  'w-full px-2 py-1.5 text-sm rounded-md border border-ink-300 dark:border-ink-700 ' +
  'bg-white dark:bg-ink-900 focus:outline-none focus:ring-2 focus:ring-accent-500';

function FormField({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="min-w-0">
      <label className="text-[11px] uppercase tracking-wide text-ink-500">{label}</label>
      <div className="mt-0.5">{children}</div>
    </div>
  );
}

export function BankAccountCreateDrawer({
  wsId,
  open,
  onOpenChange,
  onCreated,
}: {
  wsId: string;
  open: boolean;
  onOpenChange: (next: boolean) => void;
  onCreated?: (id: string) => void;
}) {
  const { t } = useTranslation();
  const create = useCreateBankAccount(wsId);
  const [displayName, setDisplayName] = useState('');
  const [bankCode, setBankCode] = useState('');
  const [branchCode, setBranchCode] = useState('');
  const [accountKind, setAccountKind] = useState('普通');
  const [accountNumber, setAccountNumber] = useState('');
  const [currency, setCurrency] = useState('JPY');
  const [error, setError] = useState<string | null>(null);

  const onSubmit = async () => {
    setError(null);
    if (!displayName.trim()) {
      setError(t('bankFeed.errorNameRequired'));
      return;
    }
    try {
      const r = await create.mutateAsync({
        displayName: displayName.trim(),
        bankCode: bankCode.trim() || undefined,
        branchCode: branchCode.trim() || undefined,
        accountKind: accountKind.trim() || undefined,
        accountNumber: accountNumber.trim() || undefined,
        currency,
      });
      setDisplayName('');
      setBankCode('');
      setBranchCode('');
      setAccountNumber('');
      onCreated?.(r.id);
      onOpenChange(false);
    } catch (err: any) {
      setError(err?.response?.data?.message ?? err?.message ?? 'Create failed');
    }
  };

  return (
    <Drawer
      open={open}
      onOpenChange={onOpenChange}
      size="md"
      title={t('bankFeed.addAccount')}
      footer={
        <div className="flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            className="px-3 py-1.5 rounded-lg border border-ink-300 dark:border-ink-700 text-xs font-medium hover:bg-ink-50 dark:hover:bg-ink-900"
          >
            {t('common.cancel')}
          </button>
          <button
            type="button"
            onClick={onSubmit}
            disabled={create.isPending}
            className="px-4 py-1.5 rounded-lg bg-accent-600 text-white text-xs font-medium hover:bg-accent-700 disabled:opacity-50"
          >
            {t('common.save')}
          </button>
        </div>
      }
    >
      <div className="space-y-4">
        {error && (
          <div className="text-sm text-warn-600 rounded-lg border border-warn-300 bg-warn-50 dark:bg-warn-900/20 px-3 py-2">
            {error}
          </div>
        )}
        <FormField label={t('bankFeed.displayName')}>
          <input
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            placeholder="みずほ 本店 1234567"
            className={inputCn}
          />
        </FormField>
        <div className="grid grid-cols-3 gap-3">
          <FormField label={t('bankFeed.bankCode')}>
            <input value={bankCode} onChange={(e) => setBankCode(e.target.value)} placeholder="0001" className={inputCn} />
          </FormField>
          <FormField label={t('bankFeed.branchCode')}>
            <input value={branchCode} onChange={(e) => setBranchCode(e.target.value)} placeholder="001" className={inputCn} />
          </FormField>
          <FormField label={t('bankFeed.accountKind')}>
            <select value={accountKind} onChange={(e) => setAccountKind(e.target.value)} className={inputCn}>
              <option value="普通">普通 (Ordinary)</option>
              <option value="当座">当座 (Current)</option>
              <option value="貯蓄">貯蓄 (Savings)</option>
            </select>
          </FormField>
        </div>
        <FormField label={t('bankFeed.accountNumber')}>
          <input
            value={accountNumber}
            onChange={(e) => setAccountNumber(e.target.value.replace(/\D/g, ''))}
            placeholder="1234567"
            className={inputCn}
            inputMode="numeric"
          />
        </FormField>
        <FormField label={t('inbox.field.currency')}>
          <select value={currency} onChange={(e) => setCurrency(e.target.value)} className={inputCn}>
            {CURRENCIES.map((c) => (
              <option key={c.code} value={c.code}>
                {c.code} — {c.symbol}
              </option>
            ))}
          </select>
        </FormField>
      </div>
    </Drawer>
  );
}
